import { useMemo, useState } from 'react'
import { parseAsString, useQueryState } from 'nuqs'
import { Link, useNavigate } from '@tanstack/react-router'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import { RotateCcw, ChevronRight, Route } from 'lucide-react'
import { Skeleton } from '@deepguide-ai/ui/skeleton'
import { Progress } from '@deepguide-ai/ui/progress'
import { Badge } from '@deepguide-ai/ui/badge'
import { Button } from '@deepguide-ai/ui/button'
import { Checkbox } from '@deepguide-ai/ui/checkbox'
import { toast } from 'sonner'
import { trpc } from '@/lib/trpc'
import { cn } from '@/lib/utils'
import { frameworkOptions } from '@/lib/compliance-framework'
import { useFeatureFlags } from '@/hooks/useFeatureFlag'
import {
  useGuidedPath,
  type GuidedChapter,
  type GuidedStep,
} from '@/hooks/useGuidedPath'
import { GuideStepDot } from '@/components/screenata/guide/GuideStepDot'
import { isWizardScreenKey } from '@/components/screenata/onboarding/screens/buildContextScreens'

/**
 * RFC-231 — the Setup guide: a book, not a list. Chapter TOC on the left, the
 * open chapter's steps on the right, every step with a why and two actions.
 * Nothing is locked; `recommendedAfter` renders as a hint. Every `done` comes
 * from the server snapshot via `useGuidedPath`.
 */
export function SetupGuidePage() {
  const guide = useGuidedPath()
  const { workspaceId, snapshot, chapters, isReady, eligible } = guide
  // `?chapter=` is the selected chapter: shareable, and each pick is a history
  // entry so Back returns to the previous chapter.
  const [chapterKey, setChapterKey] = useQueryState(
    'chapter',
    parseAsString.withOptions({ history: 'push' }),
  )

  // Without ?chapter= open on the first chapter that still has work. The
  // default is derived, never written to the URL — only a user's pick is.
  const firstIncomplete = useMemo(
    () => chapters.find((c) => !c.complete)?.key ?? chapters[0]?.key ?? null,
    [chapters],
  )
  const selectedChapterKey = chapterKey ?? firstIncomplete

  const titlesByKey = useMemo(
    () => new Map(chapters.flatMap((c) => c.steps.map((s) => [s.key, s.title] as const))),
    [chapters],
  )

  if (!isReady) return <div className='p-6' />
  if (!eligible) {
    return (
      <div className='mx-auto max-w-2xl p-8 text-sm text-muted-foreground'>
        Getting started is for workspace admins.
      </div>
    )
  }
  if (!snapshot?.programId) return null

  // Chapter 0 is Vera's own work; once it's done it is only noise in the nav.
  const visibleChapters = chapters.filter((c) => !(c.auto && c.complete))
  const chapter = visibleChapters.find((c) => c.key === selectedChapterKey) ?? visibleChapters[0]

  return (
    <div className='mx-auto w-full max-w-[1040px] px-6 py-6'>
      <h1 className='mb-3 font-heading text-[19px] font-semibold tracking-[-0.01em] text-foreground'>
        Getting started
      </h1>
      <div className='mb-5 flex items-center gap-3'>
        <div className='h-1.5 flex-1 overflow-hidden rounded-full bg-muted'>
          <div
            className='h-full rounded-full bg-primary transition-[width]'
            style={{ width: `${guide.pct}%` }}
          />
        </div>
        <div
          data-testid='guide-progress-count'
          className='shrink-0 text-[12px] text-muted-foreground tabular-nums'
        >
          <b className='text-foreground'>{guide.completedCount}</b> of {guide.totalCount} done
        </div>
      </div>

      {snapshot.frameworks.length === 0 ? (
        <FrameworkPicker />
      ) : (
        <div className='grid md:grid-cols-[260px_1fr]'>
          <nav
            aria-label='Chapters'
            className='flex flex-row flex-wrap gap-1 py-2 md:sticky md:top-4 md:max-h-[calc(100vh-6rem)] md:flex-col md:overflow-y-auto md:border-r md:pr-4'
          >
            {visibleChapters.map((c) => {
              const i = chapters.indexOf(c)
              const done = c.complete
              const selected = c.key === chapter?.key
              return (
                <button
                  key={c.key}
                  type='button'
                  onClick={() => setChapterKey(c.key)}
                  aria-current={selected ? 'page' : undefined}
                  className={cn(
                    'relative flex w-auto items-center gap-2 rounded-sm px-2 py-1.5 pl-3 text-left transition-colors hover:bg-muted/60 md:w-full',
                    // One signal, not four: a cobalt left bar over the app's
                    // standard selected tint. Grey-on-grey read as hover, but a
                    // border + saturated fill + accented badge stacked into a
                    // slab — the bar alone is what the eye finds when scanning.
                    selected &&
                      'bg-selected hover:bg-selected before:absolute before:inset-y-1 before:left-0 before:w-[3px] before:rounded-full before:bg-cobalt-strong',
                  )}
                >
                  <span
                    className={cn(
                      'inline-flex h-5 w-5 shrink-0 items-center justify-center rounded-full border text-[10px] font-bold',
                      // Completion outranks selection — a green ✓ is the more
                      // useful fact about a chapter you are already looking at.
                      done
                        ? 'border-status-green bg-status-green-bg text-status-green'
                        : 'border-border text-muted-foreground',
                      c.auto && !done && 'border-dashed',
                    )}
                  >
                    {done ? '✓' : i}
                  </span>
                  <span className='min-w-0'>
                    <span
                      className={cn(
                        'block text-[12.5px] font-semibold leading-tight',
                        selected ? 'text-cobalt-strong' : 'text-foreground',
                      )}
                    >
                      {c.title}
                    </span>
                    <span className='text-[10.5px] text-text-placeholder tabular-nums'>
                      {c.total > 0 ? `${c.done}/${c.total}` : 'Done'}
                    </span>
                  </span>
                </button>
              )
            })}
          </nav>

          <section className='min-w-0 py-2 md:pl-6'>
            {guide.complete && chapter?.key === visibleChapters[0]?.key && (
              <div className='mb-4 rounded-md bg-status-green-bg px-4 py-5 text-center'>
                <div className='text-[17px] font-semibold text-foreground'>You're all set</div>
                <p className='text-[12.5px] text-muted-foreground'>
                  Every recommended step is done. Vera keeps monitoring; come back here any time to see the path.
                </p>
              </div>
            )}
            {chapter && (
              <ChapterView
                index={chapters.indexOf(chapter)}
                chapter={chapter}
                workspaceId={workspaceId!}
                setupPhase={snapshot.setup.phase}
                titlesByKey={titlesByKey}
                setStepSkipped={guide.setStepSkipped}
                isSkipping={guide.isSkipping}
              />
            )}
          </section>
        </div>
      )}
    </div>
  )
}

// The home journey exposes operational areas individually; the guide retains its educational chapters.
const JOURNEY_GROUPS = [
  {
    title: 'Add your company details',
    keys: ['company'],
  },
  { title: 'Configure your team', keys: ['people'] },
  { title: 'Connect your stack', keys: ['connect'] },
  {
    title: 'Review & approve policies',
    keys: ['policies'],
    primary: 'policies.approve',
  },
  {
    title: 'Implement your controls',
    keys: ['controls', 'keyDocuments'],
    primary: 'controls.readiness',
  },
  { title: 'Assess your assets', keys: ['assets'] },
  {
    title: 'Assess your vendors',
    keys: ['vendors'],
    primary: 'vendors.assess',
  },
  { title: 'Treat your risks', keys: ['risks'], primary: 'risks.treat' },
  { title: 'Triage findings', keys: ['monitor'], primary: 'monitor.findings' },
  {
    title: 'Prepare for audit',
    keys: ['audit'],
  },
  { title: 'Publish your Trust Center', keys: ['trust'] },
]

export function ReadinessJourney({
  controls,
}: {
  controls?: GuidedStep['coverage']
} = {}) {
  const guide = useGuidedPath()
  const { snapshot, workspaceId } = guide
  const companySteps = guide.chapters
    .filter((chapter) => chapter.key === 'company')
    .flatMap((chapter) => chapter.steps)
  const auditSteps = guide.chapters
    .filter((chapter) => chapter.key === 'audit')
    .flatMap((chapter) => chapter.steps)
  const peopleSteps = guide.chapters
    .filter((chapter) => chapter.key === 'people')
    .flatMap((chapter) => chapter.steps)
  const assetSteps = guide.chapters
    .filter((chapter) => chapter.key === 'assets')
    .flatMap((chapter) => chapter.steps)
  const connectionSteps = guide.chapters
    .filter((chapter) => chapter.key === 'connect')
    .flatMap((chapter) => chapter.steps)
  const measuredSteps: Record<string, GuidedStep[]> = {
    company: companySteps.filter((step) => step.key !== 'company.scope'),
    people: peopleSteps.filter((step) => step.key !== 'people.accessReview'),
    connect: connectionSteps.filter((step) => step.key !== 'connect.mdm'),
    assets: [
      ...assetSteps
        .filter((step) => step.key === 'assets.inventory')
        .map((step) => ({ ...step, title: 'Add assets' })),
      ...connectionSteps
        .filter((step) => step.key === 'connect.mdm')
        .map((step) => ({ ...step, title: 'Enroll device reporting' })),
      ...assetSteps.filter((step) => step.key !== 'assets.inventory'),
    ],
    keyDocuments: auditSteps.filter((step) => step.key === 'audit.keyDocuments'),
    audit: auditSteps.filter((step) => step.key !== 'audit.keyDocuments'),
    controls: [
      {
        key: 'controls.readiness',
        title: 'Implement and approve applicable controls',
        why: 'Complete each control’s required tests and approve the supporting evidence. Control progress follows the same scope and evidence rules as the Controls page.',
        to: '/workspaces/$workspaceId/controls',
        coverage: controls,
        done:
          !!controls && controls.total > 0 && controls.done >= controls.total,
        skippable: false,
        skipped: false,
      },
      ...peopleSteps.filter((step) => step.key === 'people.accessReview'),
    ],
  }
  const groups = JOURNEY_GROUPS.map((group) => {
    const steps = group.keys
      .flatMap(
        (key) =>
          measuredSteps[key] ??
          guide.chapters
            .filter((chapter) => chapter.key === key)
            .flatMap((chapter) => chapter.steps)
      )
      .filter(
        (step) =>
          ![
            'company.slack',
            'connect.firstpass',
            'vendors.register',
            'risks.register',
          ].includes(step.key)
      )
    const complete =
      steps.length > 0 &&
      steps.every((step) =>
        step.key === 'people.accessReview' && step.skipped
          ? true
          : step.coverage
          ? step.coverage.total > 0 && step.coverage.done >= step.coverage.total
          : step.done
      )
    return { ...group, steps, complete }
  }).filter((group) => group.steps.length > 0)
  const next = groups.find((group) => !group.complete)
  return (
    <section
      className='bg-card order-1 min-w-0 overflow-hidden rounded-md border'
      aria-label='Readiness journey'
    >
      <div className='p-4'>
        <h2 className='flex items-center gap-2 text-sm font-semibold'>
          <Route aria-hidden='true' className='text-muted-foreground size-4' />
          Readiness journey
        </h2>
        <p className='text-muted-foreground mt-1 text-xs'>
          Complete the work across your current scope.
        </p>
      </div>
      {!guide.isReady ? (
        <div role='status' aria-label='Loading readiness journey' aria-busy='true' className='px-4 pb-2'>
          <span className='sr-only'>Loading coverage…</span>
          <div aria-hidden='true'>
            {Array.from({ length: JOURNEY_GROUPS.length }).map((_, i) => (
              <div key={i} className='relative ml-3 py-4 pl-6 before:absolute before:top-0 before:-bottom-px before:left-0 before:w-px before:bg-border first:before:top-7 last:before:bottom-auto last:before:h-7 only:before:hidden'>
                <Skeleton className='absolute top-4 -left-3 size-6 rounded-full' />
                <Skeleton className='h-4 w-3/4' />
                <Skeleton className='mt-2 h-3 w-16' />
                <Skeleton className='mt-1.5 h-1 w-full' />
              </div>
            ))}
          </div>
        </div>
      ) : guide.isError ? (
        <div role='alert' className='p-4 text-xs'>
          Coverage unavailable.{' '}
          <Button variant='outline' size='sm' onClick={guide.retry}>
            Retry coverage
          </Button>
        </div>
      ) : !guide.eligible ? (
        <p className='text-muted-foreground p-4 text-xs'>
          Workspace admins manage this journey.
        </p>
      ) : !snapshot?.programId ? (
        <div className='space-y-3 p-4'>
          <p className='text-muted-foreground text-xs'>
            Create your compliance program to define scope and start the
            journey.
          </p>
          <Button asChild size='sm'>
            <Link
              to='/workspaces/$workspaceId/onboarding'
              params={{ workspaceId: workspaceId! }}
            >
              Set up your program
            </Link>
          </Button>
        </div>
      ) : snapshot.frameworks.length === 0 ? (
        <div className='space-y-3 p-4'>
          <p className='text-muted-foreground text-xs'>
            Define your audit scope to see applicable work.
          </p>
          <FrameworkPicker />
        </div>
      ) : (
        <div className='px-4 pb-2'>
          {groups.map((group, index) => {
            const primary = group.steps.find(
              (step) => step.key === group.primary
            )
            const coverage = primary?.coverage
            const unknown = primary?.key.endsWith('.readiness') && !coverage
            const completed = group.steps.filter((step) => step.done).length
            const done = coverage?.done ?? completed
            const total = coverage?.total ?? group.steps.length
            const label = unknown
              ? 'Progress unavailable'
              : coverage && total === 0
                ? coverage.notApplicable
                  ? `N/A · ${coverage.notApplicable} scoped out`
                  : 'No applicable items yet'
                : `${done} / ${total}${coverage ? '' : ' tasks'}`
            return (
              <details
                key={group.title}
                open={group === next || undefined}
                className='group/stage relative ml-3 pl-6 before:absolute before:top-0 before:-bottom-px before:left-0 before:w-px before:bg-border first:before:top-7 last:before:bottom-auto last:before:h-7 only:before:hidden'
              >
                <summary className='focus-visible:outline-ring cursor-pointer list-none py-4 text-xs focus-visible:outline-2 [&::-webkit-details-marker]:hidden'>
                  <span
                    className={cn(
                      'bg-card absolute top-4 -left-3 flex size-6 items-center justify-center rounded-full border text-[10px]',
                      group.complete
                        ? 'border-status-green text-status-green'
                        : group === next
                          ? 'border-status-amber-emphasis text-status-amber-emphasis'
                          : 'border-border-divider text-muted-foreground'
                    )}
                  >
                    {group.complete ? '✓' : index + 1}
                  </span>
                  <span className='flex items-center gap-2'>
                    <span className='min-w-0 flex-1 font-medium'>
                      {group.title}
                    </span>
                    <ChevronRight
                      aria-hidden='true'
                      className='text-muted-foreground size-3 shrink-0 group-open/stage:rotate-90'
                    />
                  </span>
                  <span className='text-muted-foreground mt-2 block text-[11px] tabular-nums'>
                    {label}
                  </span>
                  {!unknown && total > 0 && (
                    <Progress
                      aria-label={`${group.title} progress`}
                      aria-valuenow={Math.round((done / total) * 100)}
                      value={Math.round((done / total) * 100)}
                      className='mt-1.5 h-1'
                      indicatorClassName='bg-status-green'
                    />
                  )}
                </summary>
                <div className='pb-4'>
                  {group.steps.map((step) => (
                    <div key={step.key} className='grid grid-cols-[18px_minmax(0,1fr)] gap-3 py-4'>
                      <GuideStepDot
                        done={step.coverage
                          ? step.coverage.total > 0 && step.coverage.done >= step.coverage.total
                          : step.done}
                        className={cn('mt-0', step.key === 'people.accessReview' && step.skipped && 'border-dashed')}
                      />
                      <div className='min-w-0'>
                        <div className='flex flex-wrap items-start justify-between gap-2 text-xs'>
                          <Link
                            to={step.to as never}
                            params={{ workspaceId } as never}
                            search={(step.search ?? {}) as never}
                            className='text-foreground hover:text-status-green min-w-0 font-medium hover:underline'
                          >
                            {step.title} ↗
                          </Link>
                          <span className='text-muted-foreground tabular-nums'>
                            {step.key === 'people.accessReview' && step.skipped
                              ? 'Skipped in app'
                              : step.coverage
                              ? step.coverage.total > 0
                                ? `${step.coverage.done} / ${step.coverage.total}`
                                : step.coverage.notApplicable
                                  ? `N/A · ${step.coverage.notApplicable} scoped out`
                                  : 'No applicable items yet'
                              : step.key.endsWith('.readiness')
                                ? 'Progress unavailable'
                                : step.done
                                  ? 'Complete'
                                  : 'Pending'}
                          </span>
                        </div>
                        <p className='text-muted-foreground mt-2 text-[11px]'>
                          {step.why}
                        </p>
                        {step.key === 'people.accessReview' && step.skippable && !step.done && (
                          <div className='mt-2 space-y-1'>
                            <Button
                              variant='ghost'
                              size='sm'
                              className='h-auto px-0 py-1 text-[11px]'
                              disabled={guide.isSkipping}
                              onClick={() => guide.setStepSkipped(step.key, !step.skipped)}
                            >
                              {step.skipped ? 'Use built-in access review' : 'Skip (use own evidence)'}
                            </Button>
                            {step.skipped && (
                              <p className='text-muted-foreground text-[11px]'>
                                Attach your external review evidence to the applicable control. Its approval still counts toward control progress.
                              </p>
                            )}
                          </div>
                        )}
                        {step.coverage && step.coverage.total > 0 && (
                          <Progress
                            aria-label={`${step.title} completion`}
                            aria-valuenow={Math.round((step.coverage.done / step.coverage.total) * 100)}
                            value={Math.round(
                              (step.coverage.done / step.coverage.total) * 100
                            )}
                            className='mt-2 h-1'
                            indicatorClassName='bg-status-green'
                          />
                        )}
                        {step.subSteps
                          ?.filter((sub) => sub.key.startsWith('vendor:'))
                          .map((sub) => (
                            <Link
                              key={sub.key}
                              to='/workspaces/$workspaceId/vendors/$vendorId'
                              params={{
                                workspaceId: workspaceId!,
                                vendorId: sub.key.slice('vendor:'.length),
                              }}
                              className='text-status-green mt-2 block text-[11px] hover:underline'
                            >
                              Assess {sub.title} →
                            </Link>
                          ))}
                        {step.key === 'audit.cycles' && step.subSteps?.map((sub) => (
                          <Link
                            key={sub.key}
                            to='/workspaces/$workspaceId/audits/$auditCycleId'
                            params={{ workspaceId: workspaceId!, auditCycleId: sub.key.slice('audit:'.length) }}
                            className='text-status-green mt-2 block text-[11px] hover:underline'
                          >
                            {sub.title} ↗
                          </Link>
                        ))}
                      </div>
                    </div>
                  ))}
                </div>
              </details>
            )
          })}
        </div>
      )}
      <p className='bg-surface-subtle text-muted-foreground border-t p-4 text-[10px]'>
        Progress follows current scope. New items can reopen completed work.
        Scoped-out items do not count toward completion.
      </p>
    </section>
  )
}

function ChapterView({
  index,
  chapter,
  workspaceId,
  setupPhase,
  titlesByKey,
  setStepSkipped,
  isSkipping,
}: {
  index: number
  chapter: GuidedChapter
  workspaceId: string
  setupPhase: 'queued' | 'controls' | 'tests' | 'done' | 'failed'
  titlesByKey: Map<string, string>
  setStepSkipped: (key: string, skipped: boolean) => void
  isSkipping: boolean
}) {
  // Skipped steps keep their position — struck through with a Restore action
  // (a skip is a decision about that step, not a reason to reorder the list).
  const current = chapter.steps.find((s) => !s.done && !s.auto && !s.skipped)
  const streaming = chapter.auto && setupPhase !== 'done' && setupPhase !== 'failed'
  return (
    <>
      <h2 className='text-[15px] font-semibold text-foreground'>
        {index} · {chapter.title}{' '}
        <span className='text-[11px] font-normal text-text-placeholder tabular-nums'>
          {chapter.total > 0 ? `${chapter.done}/${chapter.total}` : 'Done'}
        </span>
      </h2>
      <p className='mb-3 text-[12.5px] text-muted-foreground'>{chapter.why}</p>
      {chapter.auto && setupPhase !== 'done' && <SetupNotice phase={setupPhase} />}
      {chapter.steps.map((step) => (
        <StepRow
          key={step.key}
          step={step}
          workspaceId={workspaceId}
          isCurrent={current?.key === step.key}
          running={streaming && !step.done}
          recommendedAfterTitle={step.recommendedAfter ? titlesByKey.get(step.recommendedAfter) : undefined}
          setStepSkipped={setStepSkipped}
          isSkipping={isSkipping}
        />
      ))}
    </>
  )
}

/** The onboarding wizard is the only destination that takes a `?step=` key. */
const isOnboardingRoute = (to: string) => to.endsWith('/onboarding')

/**
 * Sub-step state → badge colour. Anything unlisted falls back to blue, which
 * is the "in flight" reading (a policy's `drafted`, a cycle's `in progress`).
 */
const SUB_STEP_BADGE: Record<string, 'green' | 'default' | 'amber' | 'blue'> = {
  approved: 'green',
  provided: 'green',
  invited: 'green',
  confirmed: 'green',
  answered: 'green',
  not_started: 'default',
  not_provided: 'default',
  'not invited': 'default',
  pending: 'default',
  stale: 'amber',
}

// Type scale for a step row's actions; `size='xxs'` now carries the padding.
// Kept in one constant so the row's buttons can't drift apart.
const STEP_BUTTON = 'text-[11.5px]'

function StepRow({
  step,
  workspaceId,
  isCurrent,
  running,
  recommendedAfterTitle,
  setStepSkipped,
  isSkipping,
}: {
  step: GuidedStep
  workspaceId: string
  isCurrent: boolean
  running: boolean
  recommendedAfterTitle?: string
  setStepSkipped: (key: string, skipped: boolean) => void
  isSkipping: boolean
}) {
  const navigate = useNavigate()
  const goLabel = step.to.split('/').pop()?.replace(/-/g, ' ') ?? 'page'
  // A step may target a tab rather than a page default (People → training), so
  // its `search` travels with the destination.
  const goToStep = () =>
    navigate({
      to: step.to as never,
      params: { workspaceId } as never,
      search: (step.search ?? {}) as never,
    })
  return (
    <div
      className={cn('flex gap-3 border-t py-3 first-of-type:border-t-0', step.skipped && !step.coverage && 'opacity-70')}
      data-step={step.key}
    >
      <GuideStepDot done={step.done} current={isCurrent} running={running} />
      <div className='min-w-0 flex-1'>
        <div
          className={cn(
            'flex flex-wrap items-center gap-1.5 text-[13.5px] font-semibold text-foreground',
            (step.done || (step.skipped && !step.coverage)) && 'line-through decoration-foreground',
            step.skipped && 'text-muted-foreground decoration-muted-foreground',
          )}
        >
          {step.title}
          {step.skipped && (
            <Badge status='default' className='text-[10px] no-underline'>
              Skipped
            </Badge>
          )}
          {/* No "Optional" badge: every user step is skippable, so it marked
              nothing and would read as "none of this matters" on all ~30 rows.
              The Skip action is where dismissibility belongs. */}
          {step.frameworks?.map((fw) => (
            <Badge key={fw} status='default' className='text-[10px] uppercase no-underline'>
              {fw}
            </Badge>
          ))}
          {step.auto && (
            <Badge status='blue' className='text-[10px] no-underline'>
              Vera does this
            </Badge>
          )}
          {recommendedAfterTitle && !step.done && !step.skipped && (
            <Badge status='amber' className='text-[10px] no-underline'>
              Recommended after “{recommendedAfterTitle}”
            </Badge>
          )}
        </div>
        <p className='mt-0.5 mb-1.5 text-[12px] leading-relaxed text-muted-foreground'>{step.why}</p>
        {step.coverage && <p className='my-2 text-xs tabular-nums text-muted-foreground'>{step.coverage.total > 0 ? `${step.coverage.done} / ${step.coverage.total} covered` : 'No applicable items yet'}</p>}
        {step.detail && !step.done && !step.skipped && (
          <div className='mb-1.5 text-[11.5px] font-semibold text-status-blue'>{step.detail}</div>
        )}
        {step.subSteps && step.subSteps.length > 0 && !step.done && !step.skipped && (
          <ul className='mb-2 flex flex-col gap-1'>
            {step.subSteps.map((sub) => (
              <li key={sub.key} className='flex items-center gap-2 rounded-sm border bg-muted/30 px-2.5 py-1.5 text-[12.5px]'>
                <GuideStepDot done={sub.done} className='mt-0' />
                <span className={cn('min-w-0 flex-1 truncate', sub.done ? 'text-muted-foreground' : 'text-foreground')}>
                  {sub.title}
                </span>
                {sub.detail && (
                  <span className='shrink-0 text-[11px] text-muted-foreground tabular-nums'>
                    {sub.detail}
                  </span>
                )}
                {sub.state && (
                  <Badge status={SUB_STEP_BADGE[sub.state] ?? 'blue'} className='text-[10px] uppercase'>
                    {sub.state.replace(/_/g, ' ')}
                  </Badge>
                )}
                {sub.key.startsWith('vendor:') && <Link to='/workspaces/$workspaceId/vendors/$vendorId' params={{ workspaceId, vendorId: sub.key.slice('vendor:'.length) }} className='text-xs text-cobalt-strong hover:underline'>Assess</Link>}
                {sub.key.startsWith('policy:') && (
                  <Link
                    to='/workspaces/$workspaceId/policies/$policySlug'
                    params={{ workspaceId, policySlug: sub.key.slice('policy:'.length) }}
                    className='text-[11.5px] font-semibold text-status-blue hover:underline'
                  >
                    Open
                  </Link>
                )}
                {sub.testId && (
                  <Link
                    to='/workspaces/$workspaceId/tests/$testId'
                    params={{ workspaceId, testId: sub.testId }}
                    className='text-[11.5px] font-semibold text-status-blue hover:underline'
                  >
                    Open
                  </Link>
                )}
                {sub.key.startsWith('audit:') && (
                  <Link
                    to='/workspaces/$workspaceId/audits/$auditCycleId'
                    params={{ workspaceId, auditCycleId: sub.key.slice('audit:'.length) }}
                    className='shrink-0 text-[11.5px] font-semibold text-status-blue hover:underline'
                  >
                    Open
                  </Link>
                )}
                {/* Follows the step's own destination rather than hardcoding
                    the wizard: pre-onboarding that is the wizard, and the key
                    deep-links to its screen; afterwards it is Program settings,
                    which has every field on one page and needs no key. The
                    screen-key gate only matters for the wizard, where an
                    unknown key silently resolves to a different screen. */}
                {sub.key.startsWith('context:') &&
                  (!isOnboardingRoute(step.to) ||
                    isWizardScreenKey(sub.key.slice('context:'.length))) && (
                    <Link
                      to={step.to as never}
                      params={{ workspaceId } as never}
                      search={
                        (isOnboardingRoute(step.to)
                          ? { step: sub.key.slice('context:'.length) }
                          : {}) as never
                      }
                      className='shrink-0 text-[11.5px] font-semibold text-status-blue hover:underline'
                    >
                      Answer
                    </Link>
                  )}
              </li>
            ))}
          </ul>
        )}
        {/* A done step keeps its destination — the guide doubles as the map of
            where each thing lives, and "done" often means "go look at it".
            Quiet ghost styling so open work still leads the eye; Ask Vera and
            Skip drop away (both are about *doing* the step). */}
        {step.done && !step.skipped && (
          <div className='flex flex-wrap gap-1.5'>
            <Button
              variant='ghost'
              size='xxs'
              className={cn(STEP_BUTTON, 'text-muted-foreground hover:text-foreground')}
              onClick={() => goToStep()}
            >
              Go to {goLabel} →
            </Button>
          </div>
        )}
        {step.skipped && (
          <div className='flex flex-wrap gap-1.5'>
            <Button
              variant='outline'
              size='xxs'
              className={STEP_BUTTON}
              disabled={isSkipping}
              aria-label={`Restore ${step.title}`}
              onClick={() => setStepSkipped(step.key, false)}
            >
              <RotateCcw className='size-3.5' /> Restore
            </Button>
          </div>
        )}
        {/* No per-step "Ask Vera": the hero already offers it for the next
            step with this same prompt, and the header carries Vera on every
            page. A third copy on all 32 rows only out-ranked "Go to X", which
            is what actually completes the step. `veraPrompt` stays on the DTO
            — the hero reads it. */}
        {!step.done && !step.auto && !step.skipped && (
          <div className='flex flex-wrap gap-1.5'>
            <Button
              variant='outline'
              size='xxs'
              className={STEP_BUTTON}
              onClick={() => goToStep()}
            >
              Go to {goLabel} →
            </Button>
            {step.skippable && (
              <Button
                variant='ghost'
                size='xxs'
                className={STEP_BUTTON}
                disabled={isSkipping}
                aria-label={`Skip ${step.title}`}
                onClick={() => setStepSkipped(step.key, true)}
              >
                Skip
              </Button>
            )}
          </div>
        )}
      </div>
    </div>
  )
}

/**
 * Chapter 0 action while setup isn't done. `resumeSetup` is idempotent (an
 * in-flight job is reused), so the button is safe to show while Vera is
 * genuinely working — and it is the only way forward for a workspace whose
 * setup was never enqueued (created before RFC-231, or a lost queue job).
 */
function SetupNotice({ phase }: { phase: 'queued' | 'controls' | 'tests' | 'failed' }) {
  const queryClient = useQueryClient()
  const { mutate, isPending } = useMutation({
    ...trpc.complianceProgram.resumeSetup.mutationOptions(),
    onSuccess: () => {
      toast.success('Vera is setting up your program')
      void queryClient.invalidateQueries({ queryKey: trpc.complianceProgram.getGuidedPath.queryKey() })
    },
    onError: (err: unknown) =>
      toast.error(err instanceof Error ? err.message : 'Could not start setup'),
  })
  const failed = phase === 'failed'
  return (
    <div
      className={cn(
        'mb-3 flex items-center justify-between gap-3 rounded-md border px-3 py-2 text-[12.5px] text-foreground',
        failed ? 'border-status-red bg-status-red-bg' : 'border-border bg-muted/40',
      )}
    >
      <span>
        {failed
          ? 'Setup stopped before your tests were compiled.'
          : phase === 'tests'
            ? 'Controls are seeded; tests are still compiling. If nothing changes in a minute, start setup again.'
            : "Vera hasn't seeded this program yet. Start setup to compile your controls and tests."}
      </span>
      <Button size='xxs' variant='outline' className={STEP_BUTTON} disabled={isPending} onClick={() => mutate()}>
        {failed ? 'Retry setup' : 'Start setup'}
      </Button>
    </div>
  )
}

/**
 * Shown only when the workspace was created without any framework — the one
 * input setup cannot default. Enrolls, marks the program onboarded, and
 * starts the tests-first pipeline in the background.
 */
function FrameworkPicker() {
  const { flags } = useFeatureFlags(['iso27001', 'iso42001'])
  const options = useMemo(
    () => frameworkOptions(flags).filter((o) => o.value !== 'OTHER' && o.value !== 'GDPR'),
    [flags],
  )
  const [selected, setSelected] = useState<string[]>(['SOC2'])
  const queryClient = useQueryClient()
  const { mutate, isPending } = useMutation({
    ...trpc.complianceProgram.enrollFrameworksAndStart.mutationOptions(),
    onSuccess: () => {
      toast.success('Vera is setting up your program')
      void queryClient.invalidateQueries({ queryKey: trpc.complianceProgram.getGuidedPath.queryKey() })
      void queryClient.invalidateQueries({ queryKey: trpc.complianceProgram.getProgram.queryKey() })
    },
    onError: (err: unknown) =>
      toast.error(err instanceof Error ? err.message : 'Could not enroll frameworks'),
  })
  return (
    <section className='rounded-md border bg-card px-5 py-4'>
      <h2 className='text-[15px] font-semibold text-foreground'>Which frameworks are you working toward?</h2>
      <p className='mb-3 text-[12.5px] text-muted-foreground'>
        The only thing Vera needs before she can seed your controls and compile your tests. You can add or remove frameworks later.
      </p>
      <div className='flex flex-col gap-2'>
        {options.map((o) => {
          const checked = selected.includes(o.value)
          return (
            <label key={o.value} className='flex cursor-pointer items-start gap-3 rounded-sm border px-3 py-2 hover:bg-muted/40'>
              <Checkbox
                checked={checked}
                onCheckedChange={(v) =>
                  setSelected((prev) => (v ? [...prev, o.value] : prev.filter((x) => x !== o.value)))
                }
                aria-label={o.label}
              />
              <span>
                <span className='block text-[13px] font-semibold text-foreground'>{o.label}</span>
                <span className='text-[12px] text-muted-foreground'>{o.description}</span>
              </span>
            </label>
          )
        })}
      </div>
      <div className='mt-3'>
        <Button disabled={selected.length === 0 || isPending} onClick={() => mutate({ frameworks: selected as never })}>
          Start setup
        </Button>
      </div>
    </section>
  )
}
